import { useFormContext } from 'react-hook-form';
import type { ChangeEvent } from 'react';
import { useCallback, useMemo } from 'react';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { AdSetEntry } from '../api/getAdSets';

export const AdSetStatusSwitcher = (
  { cellParams }: { cellParams: GridRenderCellParams<AdSetEntry> },
) => {
  const currentId = cellParams.row.id;
  const { watch, setValue } = useFormContext();
  const selectedAdSetsRows: AdSetEntry[] = watch('selectedAdSets');
  const currentRow = selectedAdSetsRows?.find((r) => r.id === currentId);
  const isSelected = !!currentRow;
  const isChecked = currentRow?.status === 'ACTIVE';
  const labelText = isChecked ? 'Active' : 'Paused';

  const onChange = useCallback(
    (_: ChangeEvent, checked: boolean) => {
      setValue(
        'selectedAdSets',
        selectedAdSetsRows.map((adSet) => {
          if (adSet.id === currentId) {
            return { ...adSet, status: checked ? 'ACTIVE' : 'PAUSED' };
          }
          return adSet;
        }),
      );
    },
    [currentId, setValue, selectedAdSetsRows],
  );

  return (
    isSelected && (
      <FormControlLabel
        onClick={(e) => {
          e.stopPropagation();
        }}
        control={
          <Switch
            datatype={'adSetStatusSwitcher'}
            size={'small'}
            checked={isChecked}
            onChange={onChange}
            sx={{ mr: 1 }}
          />
        }
        label={labelText}
        sx={{ pl: 2 }}
      />
    )
  );
};

export const AdSetTag = (
  {
    cellParams,
  }: {
    cellParams: GridRenderCellParams<AdSetEntry>;
  }) => {
  const currentId = cellParams.row.id;
  const { watch } = useFormContext();

  const selectedAdSetsRows: AdSetEntry[] = watch('selectedAdSets');
  const selectedAdSetsDuplicatesCount: Record<string, string> = watch(
    'selectedAdSetsDuplicatesCount',
  );
  const duplicatesCount = Number(
    selectedAdSetsDuplicatesCount?.[currentId] ?? '0',
  );
  const currentRow = useMemo(
    () => selectedAdSetsRows?.find((r) => r.id === currentId),
    [selectedAdSetsRows, currentId],
  );
  const isSelected = !!currentRow;

  if (!isSelected) return null;

  return (
    <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
      {duplicatesCount && duplicatesCount > 1 ? (
        <Typography variant={'body2'}>
          -duplicate{selectedAdSetsDuplicatesCount?.[currentId] ?? '0'}
        </Typography>
      ) : null}
    </Box>
  );
};
